import { LitElement, html, css } from 'lit';
import { accessibility, grid } from '../../utilities/styles';
import { cardLinks } from '../../utilities/accessibility';
import { EventManager } from '../../utilities/events';
import { ContextConsumer } from '@lit/context';
import { organisationContext } from '../../utilities/context';
import { decodeEntities, checkFormLock, getUrlFromFileId } from '../../utilities/formsUtilities';
import { dateToMaybeRange } from '../../utilities/eventUtilities';
import { AllInResponsiveTable } from '../Utilities/AllInResponsiveTable';
import { AllInOrganisationConsumer } from '../Utilities/AllInOrganisationConsumer';
import { AllInSessionStorage } from '../Utilities/AllInSessionStorage';
import { arrowBack } from '../../utilities/icons';
import { handle403s } from '../../utilities/AllInErrorHandling';
export class AllInEvents extends AllInResponsiveTable(AllInSessionStorage(AllInOrganisationConsumer(LitElement))) {
    static properties = {
        name: { type: String },
        page: { type: Number, reflect: true },
        _loading: { state: true, type: Boolean },
        _eventData: { state: true, type: Array },
        _eventsTotal: { state: true, type: Number },
        _newEventMessage: { state: true, type: String },
        _dataProviderConfig: { state: true, type: Object },
        _fetchingProviderEvents: { state: true, type: Boolean },
    };
    constructor() {
        super();
        this._loading = true;
        this._eventsTotal = 0;
        this._newEventMessage = '';
        this._dataProviderConfig = {};
        this.providerConfigStrings = {
            'SpektrixV3': 'Spektrix',
            'Ticketsolve': 'Ticketsolve',
            'TicketsolveJSON': 'Ticketsolve',
            'TicketsolveXML': 'Ticketsolve',
            'TessituraV15': 'Tessitura',
            // Add other providers as needed
        };
    }
    async connectedCallback() {
        super.connectedCallback();
        document.querySelector('.wp-block-ace-content')?.classList.add('switch-to-one-column');
        this.eventManager.listen(`allin-change-page-${this.name}`, this._handleChangePage.bind(this));
        this.eventManager.listen(`allin-organisation-${this.name}-refresh-events`, async () => { await this._fetchEvents(); });
        if (!this.organisation) {
            this.organisation = this._getOrganisationById(this.currentOrg);
        }
        this._loading = true;
        if (this.organisation && this.organisation.hasOwnProperty('events') && this.organisation.events.hasOwnProperty(`page_${this.page}`)) {
            this._eventData = this.organisation.events[`page_${this.page}`];
            this._loading = false;
        }
        if (this.currentOrg) {
            this._maybeGetEventsFromSessionStorage();
            if (!this.organisation?.events?.[`page_${this.page}`]) {
                await this._fetchEvents();
            }
            else {
                this._fetchEvents();
            }
            if (!this.organisation.hasOwnProperty('integration_config') || !this.organisation.integration_config) {
                this._fetchIntegrationConfig();
            }
            else {
                this._dataProviderConfig = this.organisation.integration_config;
            }
        }
        if (this.organisation?.events?.[`page_${this.page}`]) {
            this._eventData = this.organisation.events[`page_${this.page}`];
            this._loading = false;
        }
        this.renderRoot.querySelector('button,a')?.focus();
        cardLinks(this.shadowRoot);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.querySelector('.wp-block-ace-content')?.classList.remove('switch-to-one-column');
        this.eventManager.disconnect();
    }
    async updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('currentOrg') && this.currentOrg) {
            this.organisation = this._getOrganisationById(this.currentOrg);
            if (this.organisation && this.organisation.hasOwnProperty('events') && this.organisation.events.hasOwnProperty(`page_${this.page}`)) {
                this._eventData = this.organisation.events[`page_${this.page}`];
                this._loading = false;
            }
            if (this.currentOrg) {
                this._maybeGetEventsFromSessionStorage();
                if (this.organisation?.events?.[`page_${this.page}`]) {
                    await this._fetchEvents();
                }
                else {
                    this._fetchEvents();
                }
            }
            if (this.organisation?.events?.[`page_${this.page}`]) {
                this._eventData = this.organisation.events[`page_${this.page}`];
                this._loading = false;
            }
        }
    }
    render() {
        let main = '';
        if (this._loading) {
            main = html `<allin-loader loading></allin-loader>`;
        }
        else {
            let table = html `<p>No events have been added yet</p>`;
            let buttonString = `Add an event`;
            const isActive = this.organisation?.subscriptionEndDate && new Date(this.organisation.subscriptionEndDate) > new Date();
            if (this._eventData?.length) {
                buttonString = `Add another event`;
                table = html `\n                    <div role="region" aria-labelledby="table_caption" tabindex="0" class="scroll-auto">\n                        <style>${this._styles?.join('')}</style>\n                        <table id="events_table">\n                            <caption id="table_caption" class="screen-reader-text">${decodeEntities(this.organisation.name)} events</caption>\n                            <tbody>\n                            <tr>\n                                <th><span class="screen-reader-text">Event image</span></th>\n                                <th>Event name</th>\n                                <th>Location</th>\n                                <th>Status</th>\n                                <th>Created by</th>\n                                <th>Date</th>\n                                <th><span class="screen-reader-text">Actions</span></th>\n                            </tr>\n                            ${this._eventData
                    .filter(event => event.status !== 'Archived')
                    .map((event) => {
                    let name = decodeEntities(event.name);
                    let id = event.id;
                    let imageName;
                    for (let i = 1; i < 4; i++) {
                        if (event[`photo${i}`]) {
                            imageName = `photo${i}`;
                            break;
                        }
                    }
                    let image = event[imageName] ? event[imageName] : '';
                    let path = image ? event[imageName].publicUrl : '';
                    let alt = image ? event[imageName].altText : '';
                    let locationName = event.location ? decodeEntities(event.location.name) : '';
                    if (!locationName && event.locationName && event.format != 'Online') {
                        locationName = decodeEntities(event.locationName);
                    }
                    if (!locationName && event.locationUrl && event.format != 'Online') {
                        locationName = html `<a href="${event.locationUrl}">Touring event</a>`;
                    }
                    if (!locationName && event.format == 'Online') {
                        locationName = html `<a href="${event.locationUrl}">Online event</a>`;
                    }
                    let dateString = '';
                    if (event.hasOwnProperty('firstInstance')) {
                        let firstInstance = event.firstInstance;
                        let lastInstance = '';
                        if (event.hasOwnProperty('lastInstance')) {
                            lastInstance = event.lastInstance;
                        }
                        dateString = dateToMaybeRange(firstInstance.start, lastInstance.start);
                    }
                    if (!path) {
                        path = '/wp-content/themes/ace/assets/fallback_new.jpeg';
                        alt = `Fallback image for event ${name}`;
                    }
                    let source = '';
                    if (event.createdBy == 'DMS_ADMIN') {
                        source = `Imported`;
                    }
                    else {
                        source = event.createdByName;
                    }
                    let buttonGroup = event.status !== 'Archived' ? html `\n                                    <a href="javascript:void(0)" data-event_id="${id}" data-org_id="${this.currentOrg}" @click=${this._handleEventClick}>Manage event</a>\n                                    <!--\n                                    <a href="javascript:void(0)" data-event_id="${id}" data-org_id="${this.currentOrg}" @click=${this._handleEventDetailsClick}>Manage details</a>\n                                    -->\n                                ` : '';
                    if (!isActive) {
                        buttonGroup = html `<a href="javascript:void(0)" data-event_id="${id}" data-org_id="${this.currentOrg}" @click=${this._handleEventClick}>Manage event</a>`;
                    }
                    return html `\n                                    <tr>\n                                        <td><img src="${path}" alt="${alt}" width="100" height="65" style="object-fit: cover;"></td>\n                                        <td><strong>${name}</strong></td>\n                                        <td>${locationName}</td>\n                                        <td class="${event.status.toLowerCase()}">${event.status}</td>\n                                        <td>${source}</td>\n                                        <td>${dateString}</td>\n                                        <td>${buttonGroup}</td>\n                                    </tr>\n                                `;
                })}\n                            </tbody>\n                        </table>\n                    </div>\n                `;
            }
            //let shouldPaginate = this.organisation?.events?.total > 9;
            let shouldPaginate = this._eventsTotal > 9;
            let paginate = '';
            if (shouldPaginate) {
                paginate = html `\n                    <allin-pagination\n                        total="${this._eventsTotal}"\n                        pageSize=9\n                        name=${this.name}\n                        currentPage=${this.page}\n                    />\n\n                `;
            }
            let button = html `<a href="javascript:void(0)" class="button" @click=${this._handleAddEventClick}>${buttonString}</a>`;
            if (this._dataProviderConfig && this._dataProviderConfig.hasOwnProperty('mappings') && this._dataProviderConfig?.provider !== 'DMS') {
                buttonString = `Add an event manually`;
                button = html `<div class="grid grid_50">\n                    <a href="javascript:void(0)" class="button" @click=${this._handleAddEventClick}>${buttonString}</a>\n                    <button class="button" @click=${this._handlePullEventsClick} ?disabled=${this._fetchingProviderEvents}>${this._fetchingProviderEvents ? `Fetching events...` : `Pull your events from ${this.providerConfigStrings[this._dataProviderConfig?.provider]}`}</button>\n                    <div></div>\n                    <div class="message notice" aria-live="polite">\n                        ${this._fetchingProviderEvents ? html `<p>Fetching events from your ${this.providerConfigStrings[this._dataProviderConfig?.provider]} account.  This may take a few minutes depending on the number of events you have. Please refresh your browser...</p>` : html `<p>This will pull all events from your ${this.providerConfigStrings[this._dataProviderConfig?.provider]} account and add them to your All In dashboard. This may take a few minutes depending on the number of events you have.</p>`}\n                    </div>\n                </div>`;
            }
            main = html `\n                <div class="grid">\n                    <div class="flex header">\n                        <h3>${decodeEntities(this.organisation.name)} events</h3>\n                        <div class="return">                  \n                            <button @click=${(e) => { e.preventDefault(); this._updateContext({ dashboard: 'dashboard' }); }} aria-label="Back to dashboard"><span aria-hidden=true>${arrowBack}</span>Back to dashboard</button>\n                        </div>\n                    </div>\n                    ${table}\n                    ${paginate}\n                    ${button}\n                    ${this._newEventMessage ? this._newEventMessage : ''}\n                </div>\n            `;
        }
        return html `${main}`;
    }
    _getOrganisationById(id) {
        return this.organisations.find((org) => org.org_id == id);
    }
    _handleAddEventClick(e) {
        e.preventDefault();
        // prevent add event for expired subscriptions
        const isActive = this.organisation?.subscriptionEndDate && new Date(this.organisation.subscriptionEndDate) > new Date();
        const btnContainer = e.currentTarget.parentElement;
        // Remove existing error message if any
        const existingMsg = btnContainer.querySelector('.add-event-error');
        if (existingMsg)
            existingMsg.remove();
        if (!isActive) {
            // Append <p> below the button
            this._newEventMessage = html `\n                <div class="message notice" aria-live="polite">\n                    <div class="flex subscriptionrenew">\n                        <p>Your subscription has expired. You can view existing events, but you cannot add new events until you renew your subscription.</p>\n                        <p><a href="javascript:void(0)" class="button" @click=${(e) => { e.preventDefault(); this._updateContext({ dashboard: 'subscriptions', panel: '' }); }}>Renew Subscription</a></p>\n                    </div>\n                </div>\n            `;
            return; // stop further action
        }
        this.eventManager.emit(`allin-organisation-${this.name}-switcher`, { dashboard: 'events', panel: `add` });
    }
    _handleEventClick(e) {
        e.preventDefault();
        this.eventManager.emit(`allin-organisation-${this.name}-switcher`, { dashboard: 'events', panel: `${e.target.dataset.event_id}`, view: '' });
    }
    _handleEventDetailsClick(e) {
        e.preventDefault();
        // prevent edit for expired subscriptions
        const isActive = this.organisation?.subscriptionEndDate && new Date(this.organisation.subscriptionEndDate) > new Date();
        const btnContainer = e.currentTarget.parentElement;
        // Remove existing error message if any
        const existingMsg = btnContainer.querySelector('.event-edit-error');
        if (existingMsg)
            existingMsg.remove();
        if (!isActive) {
            // Append <p> below the button
            const p = document.createElement('p');
            p.className = 'event-edit-error';
            p.textContent = 'Your subscription has expired. Please renew your subscription.';
            p.style.color = 'red';
            p.style.marginTop = '0.5rem';
            btnContainer.appendChild(p);
            return; // stop further action
        }
        this.eventManager.emit(`allin-organisation-${this.name}-switcher`, { dashboard: 'events', panel: `${e.target.dataset.event_id}`, view: 'edit' });
    }
    _handleChangePage(e) {
        const detail = e.detail;
        if ('page' in detail && detail.page !== this.page) {
            this.page = detail.page;
            this._fetchEvents();
        }
    }
    async _fetchEvents(noLoading = false) {
        if (!noLoading) {
            this._loading = true;
        }
        let url = `${this.restUrl}?org_id=${this.currentOrg}&action=${this.action}-events&nonce=${this.nonce}&page=${this.page}`;
        try {
            let response = await fetch(url, {
                headers: {
                    'AllIn-Nonce': this.nonce,
                }
            });
            if (response.status === 403) {
                handle403s();
                return;
            }
            let data = await response.json();
            if (!this.organisation.hasOwnProperty('events')) {
                this.organisation.events = {};
            }
            this.organisation.events[`page_${this.page}`] = data.events.data;
            data.events.data.forEach(async (event) => {
                this.organisation.events[event.id] = event;
            });
            this.organisation.events.total = data.events.total;
            this._eventData = this.organisation.events[`page_${this.page}`];
            this._eventsTotal = data.events.total;
            this._setEventsToSessionStorage();
            this._updateContext({ organisation: JSON.stringify(this.organisation) });
        }
        catch (error) {
            console.error('Error:', error);
        }
        finally {
            if (!noLoading) {
                this._loading = false;
            }
        }
    }
    async _fetchIntegrationConfig() {
        try {
            let response = await fetch(this.restUrl + `?action=get_integration_config&org_id=${this.currentOrg}&nonce=${this.nonce}`, {
                method: 'GET',
                headers: {
                    'AllIn-Nonce': this.nonce,
                }
            });
            let data = await response.json();
            if (!this.organisation.hasOwnProperty('integration_config')) {
                this.organisation.integration_config = {};
            }
            this._dataProviderConfig = data.integration_config;
            this.organisation.integration_config = data.integration_config;
            this._updateContext({ organisation: this.organisation });
        }
        catch (error) {
            console.error('Error:', error);
        }
    }
    _handlePullEventsClick() {
        this._fetchingProviderEvents = true;
        try {
            fetch(this.restUrl + `?action=pull_provider_events&org_id=${this.currentOrg}&nonce=${this.nonce}`, {
                method: 'GET',
                headers: {
                    'AllIn-Nonce': this.nonce,
                }
            });
        }
        catch (error) {
            console.error('Error:', error);
        }
        let counter = 0;
        this._fetchEvents(true);
        this._providerInterval = window.setInterval(() => {
            counter++;
            if (counter == 10) {
                clearInterval(this._providerInterval);
            }
            this._fetchEvents(true);
        }, 30000);
    }
    static styles = [
        ...super.styles,
        accessibility,
        grid,
        css `
        .bg{
            padding:var(--allin-padding,2rem);
            background-color:var(--allin-bg-color,#ccc);
            display:grid;
            gap:1rem;
            margin-bottom:1rem;
            
        }
        @media screen and (min-width:700px){
            .bg{
                grid-template-columns: auto 1fr 2rem;
            }
        }
        .button{
            text-align:center;
        }
        .grid{
            gap:1.5rem;
        }
        
        .card a{
            display:block;
            padding:1rem;
            border-radius:var(--allin-border-radius,0.5rem);
            background-color:var(--allin-highlight-color,#472961);
            color:#fff;
            text-align:center;
            font-weight:700;
            text-decoration:none;
            transition:background-color var(--transition) ease;
            height:fit-content;
            align-self:end;
        }

        .card:hover a{
            background-color:var(--allin-highlight-color-lighter,#5e3e7d);
        }

        .content{
            display:grid;
            gap:0.5rem;
        }
        .card img{
            object-fit:contain;
        }
        h4,h3,p{
            margin:0;
        }

        .button-group{
            display:flex;
            justify-content:space-between;
            gap:1rem;
        }

        .button-group button svg{
            transition:transform var(--transition) ease;
        }
        .button-group button:hover, .return:hover{
            text-decoration-thickness:2px;
        }
        
        .button-group button:hover svg, .return:hover svg{
            transform:rotate(15deg);
        }
        @media (prefers-reduced-motion: reduce) {
            .button-group button svg{
                transform: none !important;
            }
        }
        
        ul{
            list-style:none;
            padding:0;
            margin:0;
        }

        ul li{
            display:grid;
            gap:0.5rem;
        }

        .button{
            padding:1rem;
            border-radius:var(--allin-border-radius,0.5rem);
            font-family:var(--allin-font-family,'Montserrat');
            background-color:var(--allin-button-bg-color,#472961);
            font-size:var(--allin-button-font-size,1rem);
            color:var(--allin-button-text-color,#fff);
            font-weight:(var(--allin-button-font-weight,600));
            cursor:pointer;
            border:none;
            text-decoration:none;
            transition:background-color var(--transition) ease;
        }

        .header{
            justify-content:space-between;
            max-width:1300px;
        }

        .return button{
            color:var(--allin-button-bg-color,#472961);
            border:none;
            background-color:transparent;
            padding:0;
            display:flex;
            align-items:center;
            gap:0.5rem;
            font-weight:400;
            font-size:1.25rem;
            cursor:pointer;
        }

        table{
            width:100%;
        }

        table a{
            text-decoration:underline;
            color:var(--allin-link-color,#472961);
            display:block;
            font-weight:700;
            margin-bottom:0.5rem;
        }
        
        #events_table td:nth-child(1)::before, #events_table td:nth-child(6)::before{
            display:none;
        }

        @media screen and (max-width:1000px){
            #events_table td:has(img){
                aspect-ratio:16/9;
                position:relative;
            }
            #events_table td:has(img) img{
                position:absolute;
                top:0;
                left:0;
                width:100%;
                height:100%;
                object-fit:cover;
            }

            #events_table td:not(:first-of-type):not(:nth-of-type(6)){
                display:grid;
                gap:0.5rem;
                grid-template-columns: 10ch 1fr;
                padding:0;
            }
            #events_table td{
                padding:0;
            }
            #events_table tr:not(:first-of-type){
                display:grid;
                gap:1rem;
                grid-template-rows:auto auto auto auto auto 1fr;
                padding:2rem;
            }
            #events_table td img{
                object-fit: cover;
            }
        }
        .subscriptionrenew{
            gap:1rem;
            justify-content:space-between;
            flex-wrap:wrap;
        }

        `
    ];
}
